import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, TextField, Typography, } from "@mui/material";
import { TOpenModalPerfil } from "..";
import { useCallback, useEffect, useMemo, useState } from "react";
import ListaAcoes from "./ListaAcoes";
import { handleSimpleCheckbox } from "../../../../helpers/handlersGenericos";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../lib/axios";
import { IDataDetalhePerfil } from "../../../../types/api/configuracoes/detalhar-perfil";
import { enqueueSnackbar } from "notistack";
import { ICadPerfilInput } from "../../../../types/mutations/cadastrar-perfil";
import { IAttPerfilInput } from "../../../../types/mutations/atualizar-perfil";

export default function DialogCriacaoEditPerfil({
  open,
  defaultValue,
  handleClose,
}: DialogCriacaoEditPerfilProps) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<TFormPerfil>({
    nome: "",
    dadosExtras: [],
    acoesPermitidas: [],
  });

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue]);
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])
  const podeContinuar = useMemo(() => form.nome === "", [form.nome]);

  //query e mutations
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["detalhar-perfil-modal", isEdit, open],
    queryFn: () => {
      if (isEdit) {
        return axiosClient.get(`/detalhar-perfil/${valoresIniciais?.id}`);
      }
      return null;
    },
    cacheTime: 0,
  });
  const { dataDetalhePerfis }: IDetalhePerfis = useMemo(
    () => ({
      dataDetalhePerfis: data?.data,
    }),
    [data]
  );

  const { mutate: criarPerfil, isLoading: isLoadingCriarPerfil } = useMutation({
    mutationKey: "cadastrar-perfil",
    mutationFn: (inputs: ICadPerfilInput) =>
      axiosClient.post("/cadastrar-perfil", inputs),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar perfil!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-perfis"]);
      handleClose();
      enqueueSnackbar("Cadastro do perfil realizado com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: atualizarPerfil, isLoading: isLoadingAttPerfil } =
    useMutation({
      mutationKey: "atualizar-perfil",
      mutationFn: (inputs: IAttPerfilInput) =>
        axiosClient.put(`/atualizar-perfil/${valoresIniciais?.id}`, inputs),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao atualizar perfil!", { variant: "error" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-perfis"]);
        refetch();
        handleClose();
        enqueueSnackbar("Atulização do perfil realizada com sucesso!", {
          variant: "success",
        });
      },
    });
  //

  //funções manipuladoras
  const handleChangeText = useCallback(
    (prop: "nome" | "dadosExtras", value: string) => {
      setForm((prevState) => ({ ...prevState, [prop]: value }));
    },
    []
  );

  const handleChangeCheckList = useCallback(
    (valor: number) => {
      const novoDado = handleSimpleCheckbox(form.acoesPermitidas, valor);
      setForm((prevState) => ({ ...prevState, acoesPermitidas: novoDado }));
    },
    [form.acoesPermitidas]
  );

  const handleChangeCheckListBatch = useCallback((valores: number[]) => {
    setForm((prevState) => {
      const novoConjunto = new Set(prevState.acoesPermitidas);

      valores.forEach((valor) => {
        if (novoConjunto.has(valor)) {
          novoConjunto.delete(valor);
        } else {
          novoConjunto.add(valor);
        }
      });

      return { ...prevState, acoesPermitidas: Array.from(novoConjunto) };
    });
  }, []);

  const handleSubmitValues = useCallback(() => {
    const params = {
      nome: form.nome,
      parametros_extras: form.dadosExtras,
      acoes: form.acoesPermitidas,
    };

    if (isEdit) {
      atualizarPerfil(params);
    }
    if (!isEdit) {
      criarPerfil(params);
    }
  }, [criarPerfil, atualizarPerfil, isEdit, form]);
  //

  useEffect(() => {
    if (dataDetalhePerfis && isEdit) {
      setForm({
        nome: dataDetalhePerfis.nome,
        dadosExtras: dataDetalhePerfis.extras || [],
        acoesPermitidas: dataDetalhePerfis.id_acoes,
      });
    }
  }, [dataDetalhePerfis, isEdit]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{`${!isEdit ? "Adicionar" : "Editar"} perfil`}</DialogTitle>

      {isFetching && (
        <Box display="flex" justifyContent="center" mt={4} mb={8}>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && (
        <>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid
                container
                size={{xs: 12, md: 4}}
                alignSelf="flex-start"
                spacing={2}
              >
                <Grid size={{xs: 12}}>
                  <TextField
                    fullWidth
                    id="outlined-basic"
                    label="Nome"
                    variant="outlined"
                    margin="normal"
                    value={form.nome}
                    onChange={(e) => handleChangeText("nome", e.target.value)}
                  />
                </Grid>

                <Grid size={{xs: 12}}>
                  <Typography variant="h6">Solicitar dados extras</Typography>
                  <Typography variant="caption">
                    Selecione abaixo os dados extras que devem ser informados ao
                    vincular o perfil em um usuário
                  </Typography>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="info_extras">Selecionar</InputLabel>
                    <Select
                      labelId="info_extras"
                      value={form.dadosExtras}
                      multiple
                      input={<OutlinedInput label="Selecionar" />}
                      onChange={(e) =>
                        setForm((prevState) => ({
                          ...prevState,
                          dadosExtras: e.target.value as string[],
                        }))
                      }
                    >
                      <MenuItem value={"loja_id"}>Loja</MenuItem>
                      <MenuItem value={"vendedor_id"}>Vendedor</MenuItem>
                      <MenuItem value={"condicoes_pagamento_blocked_ids"}>
                        Bloquear Condições pagamento
                      </MenuItem>
                      <MenuItem value={"limite_alcada"}>
                        Limite de Alçada para liberação de crédito
                      </MenuItem>
                      <MenuItem value={"publicos_permitidos"}>
                        Públicos permitidos
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid size={{xs: 12, md: 1}}>
                <Divider orientation="vertical" />
              </Grid>

              <Grid size={{xs: 12, md: 7}}>
                <ListaAcoes
                  {...{
                    handleChangeCheckList,
                    handleChangeCheckListBatch,
                  }}
                  lista={form.acoesPermitidas}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              variant="contained"
              autoFocus
              disabled={
                podeContinuar || isLoadingCriarPerfil || isLoadingAttPerfil
              }
              onClick={handleSubmitValues}
            >
              {(isLoadingCriarPerfil || isLoadingAttPerfil) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              Confirmar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

interface DialogCriacaoEditPerfilProps {
  open: boolean;
  defaultValue: TOpenModalPerfil;
  handleClose: () => void;
}

interface IDetalhePerfis {
  dataDetalhePerfis: IDataDetalhePerfil;
}

export type TFormPerfil = {
  nome: string;
  dadosExtras: Array<string>;
  acoesPermitidas: Array<number>;
};
